import { useEffect, useRef, type RefObject } from 'react';

/**
 * Zoom by scroll and pinch, shared by the Network tab's graph and the whole
 * graph. Deliberately no buttons or slider: the gesture is the whole control.
 */

/**
 * The on-screen multiplier a mark or name is drawn at, divided back into layout
 * units: it follows the zoom from its fitted size up to `cap` and holds there,
 * and it never drops below its fitted size. `scale` and `fit` are screen pixels
 * per layout unit now and at the fit; `cap` is the most a drawn unit may become.
 */
export function grownScale(scale: number, fit: number, cap: number): number {
  if (!(scale > 0)) return 1;
  const shown = Math.min(Math.max(scale, fit), Math.max(fit, cap));
  return shown / scale;
}

/** The zoom factor one wheel event asks for. Lines and pages are turned into pixels first. */
export function wheelFactor(event: Pick<WheelEvent, 'deltaY' | 'deltaMode'>): number {
  const pixels =
    event.deltaMode === 1
      ? event.deltaY * 16
      : event.deltaMode === 2
        ? event.deltaY * 400
        : event.deltaY;
  return Math.exp(-pixels * 0.0015);
}

/**
 * Whether a wheel event is for the graph rather than the page. A trackpad pinch
 * arrives as a wheel with `ctrlKey`, and is always the graph's (left alone, the
 * browser zooms the whole page). A plain scroll is the graph's only while it can
 * still zoom that way: at a limit it goes to the page, so a reader scrolling down
 * a record is never caught by the picture. A sideways scroll is never the graph's.
 */
export function wheelIsForGraph(
  event: Pick<WheelEvent, 'deltaX' | 'deltaY' | 'ctrlKey'>,
  canZoomIn: boolean,
  canZoomOut: boolean,
): boolean {
  if (event.ctrlKey) return true;
  if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.deltaY === 0) return false;
  return event.deltaY < 0 ? canZoomIn : canZoomOut;
}

/**
 * Two-finger pinch on a touch screen, from touch events: pointer events alone
 * lose the gesture to the browser's own scrolling, and a non-passive touchmove
 * is the one place it can be claimed. One finger is left alone, so the page (or
 * the element's own pointer handling) keeps it.
 *
 * `onPinch` gets the change in finger spread since the last move and the
 * midpoint in client pixels; `onStart` runs as the second finger lands, so the
 * caller can drop a drag the first finger began.
 */
export function usePinch(
  ref: RefObject<Element | null>,
  onPinch: (factor: number, clientX: number, clientY: number) => void,
  onStart?: () => void,
): void {
  const handlers = useRef({ onPinch, onStart });
  handlers.current = { onPinch, onStart };

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let spread: number | null = null;
    const measure = (touches: TouchList) => {
      const a = touches[0]!;
      const b = touches[1]!;
      return {
        spread: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        x: (a.clientX + b.clientX) / 2,
        y: (a.clientY + b.clientY) / 2,
      };
    };
    const start = (event: Event) => {
      const touches = (event as TouchEvent).touches;
      if (touches.length !== 2) return;
      spread = measure(touches).spread;
      handlers.current.onStart?.();
    };
    const move = (event: Event) => {
      const touches = (event as TouchEvent).touches;
      if (touches.length !== 2 || spread === null) return;
      /* Not cancelable means the browser is already scrolling with the first
         finger; zooming under it as well would move the page and the picture. */
      if (!event.cancelable) return;
      event.preventDefault();
      const now = measure(touches);
      if (spread > 0 && now.spread > 0) handlers.current.onPinch(now.spread / spread, now.x, now.y);
      spread = now.spread;
    };
    const end = (event: Event) => {
      if ((event as TouchEvent).touches.length < 2) spread = null;
    };
    element.addEventListener('touchstart', start, { passive: true });
    element.addEventListener('touchmove', move, { passive: false });
    element.addEventListener('touchend', end);
    element.addEventListener('touchcancel', end);
    return () => {
      element.removeEventListener('touchstart', start);
      element.removeEventListener('touchmove', move);
      element.removeEventListener('touchend', end);
      element.removeEventListener('touchcancel', end);
    };
  }, [ref]);
}
